// ============================================================================
// STAKEHOLDER ASSETS TAB
// Displays all assets (skips, equipment, vehicles, etc.) assigned to a stakeholder.
// Fetched from work_items where item_type='asset' and contact_id matches.
// ============================================================================

'use client';

import React, { useMemo } from 'react';
import {
  Package,
  MapPin,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Clock,
  Wrench,
  Truck,
  Info,
  Building2,
} from 'lucide-react';
import type { WorkItem } from '../UniversalWorkItems';
import { useWorkItemsData } from '@/hooks/useWorkItemsData';
import { useTheme } from '@/contexts/ThemeContext';

interface StakeholderAssetsTabProps {
  workItem: WorkItem;
  isDark: boolean;
  onRefresh?: () => void;
}

interface AssetGroup {
  subtype: string;
  label: string;
  icon: React.ReactNode;
  items: WorkItem[];
}

const SUBTYPE_LABELS: Record<string, string> = {
  skip: 'Skips',
  equipment: 'Equipment',
  vehicle: 'Vehicles',
  tool: 'Tools',
  facility: 'Facilities',
  machinery: 'Machinery',
  hvac: 'HVAC',
  boiler: 'Boilers',
  generator: 'Generators',
  fire_extinguisher: 'Fire Extinguishers',
  first_aid: 'First Aid Kits',
  ppe: 'PPE',
  other: 'Other Assets',
};

function getSubtypeLabel(subtype: string | null | undefined): string {
  if (!subtype) return 'Other Assets';
  return SUBTYPE_LABELS[subtype] || subtype.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function getStatusTone(status: string | null | undefined): 'green' | 'amber' | 'red' | 'gray' {
  const s = (status || '').toLowerCase();
  if (s === 'available' || s === 'active' || s === 'operational') return 'green';
  if (s === 'deployed' || s === 'in-transit' || s === 'maintenance') return 'amber';
  if (s === 'full' || s === 'out_of_service' || s === 'retired') return 'red';
  return 'gray';
}

function StatusBadge({ status, isDark }: { status: string | null | undefined; isDark: boolean }) {
  const tone = getStatusTone(status);
  const s = (status || 'unknown').toLowerCase();
  const label = s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');

  const toneClasses = {
    green: isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700',
    amber: isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700',
    red: isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700',
    gray: isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600',
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${toneClasses[tone]}`}>
      {label}
    </span>
  );
}

function AssetCard({ item, isDark }: { item: WorkItem; isDark: boolean }) {
  const customFields = useMemo(() => {
    const raw = item.custom_fields;
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed as Record<string, unknown>;
      } catch {
        return {};
      }
    }
    return {};
  }, [item.custom_fields]);

  const locationName = item.location_name || (customFields.location_name as string | undefined) || null;
  const capacity = (customFields.capacity_m3 || customFields.capacity || customFields.asset_capacity) as string | number | undefined;
  const serial = (customFields.serial_number || customFields.skip_code || customFields.rfid_tag) as string | undefined;

  return (
    <div
      className={`rounded-xl border p-4 transition-shadow hover:shadow-sm ${
        isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className={`text-sm font-semibold truncate ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              {item.title || item.item_number || `Asset #${item.id}`}
            </h4>
            <StatusBadge status={item.status} isDark={isDark} />
          </div>

          <div className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {item.item_number && <span className="font-mono">{item.item_number}</span>}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            {locationName && (
              <div className={`flex items-center gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <MapPin className="w-3.5 h-3.5" />
                <span className="truncate max-w-[200px]">{locationName}</span>
              </div>
            )}
            {capacity && (
              <div className={`flex items-center gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <Info className="w-3.5 h-3.5" />
                <span>{String(capacity)}</span>
              </div>
            )}
            {serial && (
              <div className={`flex items-center gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <Building2 className="w-3.5 h-3.5" />
                <span className="truncate max-w-[160px]">{String(serial)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function StakeholderAssetsTab({ workItem, isDark }: StakeholderAssetsTabProps) {
  const { currentTheme } = useTheme();
  const effectiveIsDark = currentTheme?.mode === 'dark' || isDark;

  const contactId = useMemo(() => {
    const cf = workItem.custom_fields;
    const parsed =
      cf && typeof cf === 'string'
        ? (() => {
            try {
              const p = JSON.parse(cf) as unknown;
              return p && typeof p === 'object' && !Array.isArray(p) ? (p as Record<string, unknown>) : {};
            } catch {
              return {};
            }
          })()
        : cf && typeof cf === 'object' && !Array.isArray(cf)
          ? (cf as Record<string, unknown>)
          : {};
    const id = (parsed?.entity_id as string | number | undefined) || workItem.contact_id;
    return id ? String(id) : null;
  }, [workItem.custom_fields, workItem.contact_id]);

  const { items, loading } = useWorkItemsData({
    itemType: 'asset',
    contactId: contactId ?? undefined,
    autoLoad: !!contactId,
  });

  const groups = useMemo<AssetGroup[]>(() => {
    const map = new Map<string, WorkItem[]>();
    for (const item of items) {
      const subtype = item.item_subtype || 'other';
      if (!map.has(subtype)) map.set(subtype, []);
      map.get(subtype)!.push(item);
    }

    const result: AssetGroup[] = [];
    for (const [subtype, groupItems] of map) {
      const label = getSubtypeLabel(subtype);
      let icon: React.ReactNode = <Package className="w-4 h-4" />;
      if (subtype === 'skip') icon = <Truck className="w-4 h-4" />;
      else if (subtype === 'vehicle') icon = <Truck className="w-4 h-4" />;
      else if (subtype === 'equipment' || subtype === 'machinery') icon = <Wrench className="w-4 h-4" />;
      else if (subtype === 'facility') icon = <Building2 className="w-4 h-4" />;
      else if (subtype === 'hvac' || subtype === 'boiler' || subtype === 'generator') icon = <Wrench className="w-4 h-4" />;
      result.push({ subtype, label, icon, items: groupItems });
    }

    // Sort by predefined order, then alphabetically
    const order = ['skip', 'vehicle', 'equipment', 'machinery', 'facility', 'tool'];
    result.sort((a, b) => {
      const ai = order.indexOf(a.subtype);
      const bi = order.indexOf(b.subtype);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.label.localeCompare(b.label);
    });

    return result;
  }, [items]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading assets…
      </div>
    );
  }

  if (!contactId) {
    return (
      <div className={`flex flex-col items-center justify-center py-16 text-sm ${effectiveIsDark ? 'text-gray-400' : 'text-gray-500'}`}>
        <AlertTriangle className="w-8 h-8 mb-2 opacity-50" />
        <p>Unable to determine stakeholder contact.</p>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center py-16 text-sm ${effectiveIsDark ? 'text-gray-400' : 'text-gray-500'}`}>
        <Package className="w-8 h-8 mb-2 opacity-50" />
        <p>No assets assigned to this stakeholder.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1">
      {groups.map((group) => (
        <div key={group.subtype}>
          <div className="flex items-center gap-2 mb-3">
            <div className={`p-1.5 rounded-lg ${effectiveIsDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
              {group.icon}
            </div>
            <h3 className={`text-sm font-semibold ${effectiveIsDark ? 'text-gray-200' : 'text-gray-800'}`}>
              {group.label}
            </h3>
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${effectiveIsDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
              {group.items.length}
            </span>
          </div>
          <div className="space-y-3">
            {group.items.map((item) => (
              <AssetCard key={item.id} item={item} isDark={effectiveIsDark} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
